import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, MapPin, Star, Globe, Phone, Plus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { QnASection } from '../components/shared/QnASection';
import Flag from '../components/world/Flag';
import { useAuth } from '../context/AuthContext';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';
import { stitekKategorie } from '../lib/odbornik';

/**
 * Odborníci a kliniky v jedné zemi.
 *
 * Odborníci vznikli dřív než Bloom World a leží ve vlastní kolekci, ne
 * v katalogu (`/api/directory/...`). Náhled země pod mapou přesto odkazoval na
 * `/world/<zeme>/specialists`, kde katalogový výpis hledal druh „specialists",
 * nenašel ho a napsal „Takový druh záznamů neznáme." Tahle stránka tam chybí.
 *
 * Umí totéž co česká stránka odborníků: hledání, filtr oborů, řazení podle
 * hodnocení, návrh nového odborníka a otázky. Dvě věci jsou schválně jinak:
 * filtr míst jsou města z výsledků (cizí země nemá naše kraje) a ve formuláři
 * není volba státu — ten je daný stránkou.
 *
 * Karta vede na `/world/<zeme>/specialists/<id>`, kde je mapa, kontakty
 * a recenze. Na kartě zůstávají i tak adresa a telefon: kdo hledá jen číslo,
 * ať kvůli němu nemusí otvírat další stránku.
 */
export default function CountrySpecialistsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [parametry, setParametry] = useSearchParams();
  const { t, dvojjazycne } = useJazyk();
  const { user, isAdmin } = useAuth();

  const [zeme, setZeme] = useState(null);
  const [odbornici, setOdbornici] = useState([]);
  const [kategorie, setKategorie] = useState([]);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');
  const [otevrenNavrh, setOtevrenNavrh] = useState(false);

  const hledani = parametry.get('q') || '';
  const mesto = parametry.get('mesto') || '';
  const obor = parametry.get('obor') || 'all';
  const razeni = parametry.get('razeni') || 'vychozi';
  const [hledaniVstup, setHledaniVstup] = useState(hledani);

  const zmenParametr = useCallback((zmeny) => {
    const nove = new URLSearchParams(parametry);
    Object.entries(zmeny).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) nove.delete(k);
      else nove.set(k, String(v));
    });
    setParametry(nove, { replace: false });
  }, [parametry, setParametry]);

  useEffect(() => { setHledaniVstup(hledani); }, [hledani]);

  useEffect(() => {
    axios.get(`${API}/specialist-categories`)
      .then((r) => setKategorie(r.data || []))
      .catch(() => { /* bez kategorií se jen neukáže filtr oborů */ });
  }, []);

  const nacti = useCallback(async () => {
    setNacita(true);
    setChyba('');
    try {
      const z = await axios.get(`${API}/geo/countries/${slug}`);
      setZeme(z.data);
      const { data } = await axios.get(`${API}/specialists`, {
        params: {
          country_iso2: z.data.iso2,
          search: hledani || undefined,
          specialty: obor !== 'all' ? obor : undefined,
        },
      });
      setOdbornici(data || []);
    } catch (e) {
      setChyba(e?.response?.status === 404 ? 'neznamaZeme' : 'chyba');
    } finally {
      setNacita(false);
    }
  }, [slug, hledani, obor]);

  useEffect(() => { nacti(); }, [nacti]);

  const nazevZeme = dvojjazycne(zeme?.name_cs, zeme?.name_en);
  const mestoOdbornika = (o) => o.location?.city_name || o.city || '';

  // Města se počítají z výsledků. Odborníci nemají vlastní `directory-places`
  // a server jich vrací nejvýš sto, takže se to vejde i do prohlížeče.
  const mesta = useMemo(() => {
    const pocty = new Map();
    odbornici.forEach((o) => {
      const m = mestoOdbornika(o);
      if (m) pocty.set(m, (pocty.get(m) || 0) + 1);
    });
    return [...pocty.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'cs'))
      .slice(0, 24);
  }, [odbornici]);

  const videt = useMemo(() => {
    const vybrane = mesto
      ? odbornici.filter((o) => mestoOdbornika(o).toLowerCase() === mesto.toLowerCase())
      : odbornici;
    if (razeni === 'nejvyssi') return [...vybrane].sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
    if (razeni === 'nejnizsi') return [...vybrane].sort((a, b) => (a.avg_rating || 0) - (b.avg_rating || 0));
    return vybrane;
  }, [odbornici, mesto, razeni]);

  if (chyba) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-bloom-sub">
          {chyba === 'neznamaZeme' ? t('zeme.neznama') : t('vypis.chyba')}
        </p>
        <button type="button" onClick={() => navigate('/world')}
                className="mt-4 inline-flex items-center gap-1.5 text-bloom-violet font-medium">
          <ArrowLeft className="w-4 h-4" /> {t('zeme.mapaSveta')}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="country-specialists">
      <button type="button" onClick={() => navigate(`/world/${slug}`)}
              className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet">
        <ArrowLeft className="w-4 h-4" /> {t('detail.zpetNa', { zeme: nazevZeme })}
      </button>

      <header className="mt-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {zeme && <Flag emoji={zeme.flag} iso2={zeme.iso2} size="lg" />}
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-bloom-text">
              {t('sekce.specialists')}
            </h1>
            {/* Počet je z toho, co je opravdu vidět. Vypsat počet za celou zemi
                a pod tím jen záznamy z jednoho města mate. */}
            <p className="text-bloom-sub text-sm">
              {nazevZeme}
              {videt.length > 0 && ` · ${t('vypis.zaznamu', { pocet: videt.length })}`}
            </p>
          </div>
        </div>

        <NavrhOdbornika
          zeme={zeme} kategorie={kategorie} user={user} isAdmin={isAdmin}
          otevren={otevrenNavrh} nastavOtevren={setOtevrenNavrh} poUlozeni={nacti}
        />
      </header>

      <form className="mt-5"
            onSubmit={(e) => { e.preventDefault(); zmenParametr({ q: hledaniVstup, mesto: '' }); }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
          <Input value={hledaniVstup} onChange={(e) => setHledaniVstup(e.target.value)}
                 placeholder={t('vypis.hledatVZemi')} className="pl-9"
                 aria-label={t('obecne.hledat')} data-testid="odbornici-hledani" />
        </div>
      </form>

      {kategorie.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="odbornici-obory">
          <Chip aktivni={obor === 'all'} onClick={() => zmenParametr({ obor: '', mesto: '' })}>
            {t('odb.vsechnyObory')}
          </Chip>
          {kategorie.map((k) => (
            <Chip key={k.id} aktivni={obor === k.name}
                  onClick={() => zmenParametr({ obor: k.name, mesto: '' })}>
              {k.short_label || k.name}
            </Chip>
          ))}
        </div>
      )}

      {mesta.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="odbornici-mesta">
          <Chip aktivni={!mesto} onClick={() => zmenParametr({ mesto: '' })}>
            {t('vypis.celaZeme')}
          </Chip>
          {mesta.map(([nazev, pocet]) => (
            <Chip key={nazev} aktivni={mesto.toLowerCase() === nazev.toLowerCase()}
                  onClick={() => zmenParametr({ mesto: nazev })}>
              <MapPin className="w-3 h-3" />{nazev}
              <span className="opacity-70">{pocet}</span>
            </Chip>
          ))}
        </div>
      )}

      {odbornici.length > 1 && (
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-bloom-sub">{t('odb.radit')}</span>
          <Select value={razeni}
                  onValueChange={(v) => zmenParametr({ razeni: v === 'vychozi' ? '' : v })}>
            <SelectTrigger className="h-8 w-auto gap-1.5 text-xs" data-testid="odbornici-razeni">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vychozi" className="text-xs">{t('odb.vychozi')}</SelectItem>
              <SelectItem value="nejvyssi" className="text-xs">{t('odb.nejvyssiHodnoceni')}</SelectItem>
              <SelectItem value="nejnizsi" className="text-xs">{t('odb.nejnizsiHodnoceni')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {nacita ? (
        <div className="py-16 flex justify-center"><div className="spinner" /></div>
      ) : videt.length === 0 ? (
        <div className="py-14 text-center">
          <p className="text-bloom-sub">
            {hledani || mesto || obor !== 'all'
              ? t('vypis.nicNeodpovida')
              : t('okoli.zadniOdbornici')}
          </p>
          {(hledani || mesto || obor !== 'all') && (
            <p className="text-bloom-sub text-sm mt-1">{t('odb.zkusteFiltry')}</p>
          )}
        </div>
      ) : (
        <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="odbornici-seznam">
          {videt.map((o) => (
            <li key={o.id}><Karta odbornik={o} slug={slug} kategorie={kategorie} /></li>
          ))}
        </ul>
      )}

      {/* Otázky se ptají na tuhle zemi, ne na odborníky obecně – jinak by se
          otázka položená v USA ukázala i v Japonsku. */}
      {zeme && (
        <div className="mt-12">
          <QnASection
            section="specialists"
            country={zeme.iso2}
            canAnswer={isAdmin || user?.role === 'specialist'}
            canDelete={isAdmin || user?.role === 'specialist'}
            showVoteCount={false}
            accentColor="pink"
            testIdPrefix="zeme-otazka"
            addButtonLabel={t('odb.polozitOtazku')}
            dialogTitle={t('odb.novaOtazka')}
            dialogPlaceholder={t('odb.naCoSeZeptat')}
            user={user}
            categories={kategorie.map((k) => ({ id: k.name, name: k.short_label || k.name }))}
            activeCategory={obor}
          />
        </div>
      )}
    </div>
  );
}

/** Filtrační štítek. Vypadá stejně u oborů i u měst. */
function Chip({ aktivni, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${
              aktivni ? 'bg-bloom-violet text-white border-bloom-violet'
                      : 'bg-white text-bloom-sub border-border/50 hover:border-bloom-violet/40'}`}>
      {children}
    </button>
  );
}

function Karta({ odbornik: o, slug, kategorie }) {
  const { t } = useJazyk();
  const mesto = o.location?.city_name || o.city || '';
  const adresa = o.location?.address || o.address || '';

  return (
    <div className="relative h-full bg-white border border-border/40 rounded-2xl p-4
                    transition-colors hover:border-bloom-violet/40">
      {/* Odkaz se roztáhne přes celou kartu, ať se dá kliknout kamkoliv. */}
      <Link to={`/world/${slug}/specialists/${o.id}`}
            className="font-heading font-bold text-bloom-text hover:text-bloom-violet
                       after:absolute after:inset-0 after:rounded-2xl">
        {o.name}
      </Link>
      {o.specialty && (
        <p className="text-sm text-bloom-violet mt-0.5">{stitekKategorie(o.specialty, kategorie)}</p>
      )}

      {o.description && (
        <p className="mt-2 text-sm text-bloom-sub leading-relaxed line-clamp-4">{o.description}</p>
      )}

      {(mesto || adresa) && (
        <p className="mt-2.5 text-xs text-bloom-sub flex items-start gap-1">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{[adresa, mesto].filter(Boolean).join(', ')}</span>
        </p>
      )}

      {o.avg_rating > 0 && (
        <p className="mt-1.5 text-xs text-bloom-sub flex items-center gap-1">
          <Star className="w-3 h-3 text-bloom-violet fill-bloom-violet" />
          {o.avg_rating.toFixed(1)}
          <span className="opacity-70">
            · {o.review_count} {t('odb.recenzi', { pocet: o.review_count })}
          </span>
        </p>
      )}

      <div className="relative mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
        {o.website && (
          <a href={o.website} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1 text-bloom-violet hover:underline">
            <Globe className="w-3 h-3" />{t('odb.web')}
          </a>
        )}
        {o.phone && (
          <a href={`tel:${o.phone}`}
             className="inline-flex items-center gap-1 text-bloom-violet hover:underline">
            <Phone className="w-3 h-3" />{o.phone}
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Návrh odborníka do téhle země.
 *
 * Správkyně přidává rovnou, ostatní posílají ke schválení — jako na české
 * stránce. Stát se nevybírá, je daný stránkou; posílá se `location`, bez které
 * by se odborník ve své zemi vůbec neukázal.
 */
function NavrhOdbornika({ zeme, kategorie, user, isAdmin, otevren, nastavOtevren, poUlozeni }) {
  const { t } = useJazyk();
  const [f, setF] = useState({
    name: '', specialty: '', description: '', address: '', city: '',
    phone: '', email: '', website: '',
  });
  const [odesila, setOdesila] = useState(false);

  useEffect(() => {
    if (kategorie.length && !f.specialty) setF((p) => ({ ...p, specialty: kategorie[0].name }));
  }, [kategorie, f.specialty]);

  // Bez ověřeného e-mailu tlačítko není, stejně jako u českých odborníků.
  if (!zeme || !user?.email_verified) return null;

  const uloz = async (e) => {
    e.preventDefault();
    if (odesila) return;
    setOdesila(true);
    const telo = {
      ...f,
      region: '',
      country: 'WORLD',
      location: { country_iso2: zeme.iso2, city_name: f.city, address: f.address },
    };
    try {
      if (isAdmin) {
        await axios.post(`${API}/specialists`, telo);
        toast.success(t('odb.pridan'));
      } else {
        await axios.post(`${API}/specialists/submit`, telo);
        toast.success(t('odb.odeslanKeSchvaleni'));
      }
      nastavOtevren(false);
      setF({
        name: '', specialty: kategorie[0]?.name || '', description: '', address: '',
        city: '', phone: '', email: '', website: '',
      });
      poUlozeni();
    } catch {
      toast.error(t(isAdmin ? 'odb.chybaPridat' : 'odb.chybaOdeslat'));
    } finally {
      setOdesila(false);
    }
  };

  const pole = (klic, klicPopisku, dalsi = {}) => (
    <div>
      <Label className="text-bloom-text text-sm">{t(klicPopisku)}</Label>
      <Input value={f[klic]} onChange={(e) => setF((p) => ({ ...p, [klic]: e.target.value }))}
             data-testid={`navrh-${klic}`} {...dalsi} />
    </div>
  );

  return (
    <Dialog open={otevren} onOpenChange={nastavOtevren}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-bloom-violet text-white shrink-0"
                data-testid="navrh-odbornika">
          <Plus className="w-4 h-4 mr-1" />
          {t(isAdmin ? 'odb.pridatOdbornika' : 'odb.navrhnout')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-bloom-text">
            {t(isAdmin ? 'odb.pridatOdbornika' : 'odb.navrhnout')}
          </DialogTitle>
          <DialogDescription>
            {t(isAdmin ? 'odb.pridejteOdbornika' : 'odb.navrhnetePopis')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={uloz} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            {pole('name', 'odb.jmeno', { required: true })}
            <div>
              <Label className="text-bloom-text text-sm">{t('odb.kategorie')}</Label>
              <Select value={f.specialty}
                      onValueChange={(v) => setF((p) => ({ ...p, specialty: v }))}>
                <SelectTrigger><SelectValue placeholder={t('odb.vyber')} /></SelectTrigger>
                <SelectContent>
                  {kategorie.map((k) => (
                    <SelectItem key={k.id} value={k.name}>{k.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-bloom-text text-sm">{t('odb.popis')}</Label>
            <Textarea value={f.description} rows={2} placeholder={t('odb.popisPriklad')}
                      onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))}
                      data-testid="navrh-description" />
          </div>

          {pole('address', 'odb.adresa', { required: true })}

          <div className="grid sm:grid-cols-2 gap-3">
            {pole('city', 'odb.mesto', { required: true })}
            <div>
              <Label className="text-bloom-text text-sm">{t('odb.stat')}</Label>
              {/* Stát se nevybírá – je daný stránkou, na které formulář stojí. */}
              <div className="h-9 flex items-center gap-2 px-3 rounded-md border border-input
                              bg-muted/40 text-sm">
                <Flag emoji={zeme.flag} iso2={zeme.iso2} size="sm" />
                {zeme.name_cs || zeme.name_en}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {pole('phone', 'odb.telefon')}
            {pole('email', 'auth.email', { type: 'email' })}
          </div>
          {pole('website', 'odb.web', { placeholder: 'https://…' })}

          {!isAdmin && <p className="text-xs text-bloom-sub">{t('odb.navrhVysvetleni')}</p>}

          <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={odesila}>
            {odesila ? t('odb.odesilam')
                     : t(isAdmin ? 'odb.pridatOdbornika' : 'odb.odeslatKeSchvaleni')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
